import { Children, isValidElement, useId, useState } from 'react';
import type { KeyboardEvent, ReactElement, ReactNode } from 'react';

export type NgTabProps = {
  id?: string;
  testID?: string;
  header: ReactNode;
  content: ReactNode;
  isVisible?: boolean;
  isDisabled?: boolean;
};

export function NgTab(props: NgTabProps) {
  void props;
  return null;
}

type TabDescriptor = Required<Pick<NgTabProps, 'isVisible' | 'isDisabled'>> &
  Pick<NgTabProps, 'header' | 'content' | 'testID'> & { key: string };

export type NgTabHeaderProps = {
  header: ReactNode;
  tabId: string;
  panelId: string;
  testID?: string;
  isDisabled?: boolean;
  isActive?: boolean;
  onActivate?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
};

export function NgTabHeader({
  header,
  tabId,
  panelId,
  testID,
  isDisabled,
  isActive,
  onActivate,
  onKeyDown,
}: NgTabHeaderProps) {
  return (
    <li className="nav-item" role="presentation">
      <button
        id={tabId}
        data-testid={testID}
        type="button"
        role="tab"
        aria-selected={isActive ?? false}
        aria-controls={panelId}
        tabIndex={isActive ? 0 : -1}
        className={`ng-tabs__header nav-link px-0 py-1 ${isActive ? 'active' : ''}`}
        disabled={isDisabled}
        onClick={onActivate}
        onKeyDown={onKeyDown}
      >
        {header}
      </button>
    </li>
  );
}

export type NgTabHeadersProps = {
  tabs: readonly TabDescriptor[];
  activeTabKey?: string;
  getTabId: (key: string) => string;
  getPanelId: (key: string) => string;
  onTabSelected?: (key: string) => void;
};

export function NgTabHeaders({
  tabs,
  activeTabKey,
  getTabId,
  getPanelId,
  onTabSelected,
}: NgTabHeadersProps) {
  const selectableTabs = tabs.filter((tab) => tab.isVisible && !tab.isDisabled);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentKey: string) => {
    if (selectableTabs.length === 0) {
      return;
    }
    const currentIndex = selectableTabs.findIndex((tab) => tab.key === currentKey);
    if (currentIndex === -1) {
      return;
    }

    let targetIndex: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        targetIndex = (currentIndex + 1) % selectableTabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        targetIndex = (currentIndex - 1 + selectableTabs.length) % selectableTabs.length;
        break;
      case 'Home':
        targetIndex = 0;
        break;
      case 'End':
        targetIndex = selectableTabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const targetKey = selectableTabs[targetIndex]?.key;
    if (targetKey == null) {
      return;
    }
    if (targetKey !== currentKey) {
      onTabSelected?.(targetKey);
    }
    document.getElementById(getTabId(targetKey))?.focus();
  };

  return (
    <div className="bg-white position-sticky top-0 z-1">
      <ul className="ng-tabs__headers nav nav-custom-tabs pb-0 pt-1 mb-2" role="tablist">
        {tabs
          .filter((tab) => tab.isVisible)
          .map((tab) => (
            <NgTabHeader
              key={tab.key}
              header={tab.header}
              tabId={getTabId(tab.key)}
              panelId={getPanelId(tab.key)}
              testID={tab.testID}
              isDisabled={tab.isDisabled}
              isActive={tab.key === activeTabKey}
              onActivate={() => onTabSelected?.(tab.key)}
              onKeyDown={(event) => handleKeyDown(event, tab.key)}
            />
          ))}
      </ul>
    </div>
  );
}

export type NgTabPanelProps = {
  children?: ReactNode;
  maxHeight?: string;
  onTabChanged?: (tabId: string) => void | Promise<void>;
};

export function NgTabPanel({ children, maxHeight = '100%', onTabChanged }: NgTabPanelProps) {
  const baseId = useId();
  const getTabId = (key: string) => `${baseId}tab-${key}`;
  const getPanelId = (key: string) => `${baseId}panel-${key}`;

  const tabs: TabDescriptor[] = [];
  Children.forEach(children, (child, index) => {
    if (isValidElement(child) && child.type === NgTab) {
      const props = (child as ReactElement<NgTabProps>).props;
      tabs.push({
        key: props.id ?? `tab-${index}`,
        testID: props.testID,
        header: props.header,
        content: props.content,
        isVisible: props.isVisible ?? true,
        isDisabled: props.isDisabled ?? false,
      });
    }
  });

  const [activeTabKey, setActiveTabKey] = useState<string | undefined>(undefined);
  const effectiveActiveTabKey = activeTabKey ?? tabs[0]?.key;
  const activeTab = tabs.find((tab) => tab.key === effectiveActiveTabKey);

  const activateTab = (key: string) => {
    const tab = tabs.find((candidate) => candidate.key === key);
    if (!tab || tab.key === effectiveActiveTabKey || tab.isDisabled) {
      return;
    }
    setActiveTabKey(key);
    void onTabChanged?.(key);
  };

  return (
    <>
      <div className="ng-tabs d-flex flex-column overflow-hidden" style={{ maxHeight }}>
        <NgTabHeaders
          tabs={tabs}
          activeTabKey={effectiveActiveTabKey}
          getTabId={getTabId}
          getPanelId={getPanelId}
          onTabSelected={activateTab}
        />
        <div
          className="ng-tabs__content overflow-auto flex-grow-1"
          role="tabpanel"
          id={effectiveActiveTabKey != null ? getPanelId(effectiveActiveTabKey) : undefined}
          aria-labelledby={
            effectiveActiveTabKey != null ? getTabId(effectiveActiveTabKey) : undefined
          }
          tabIndex={0}
        >
          {activeTab?.content}
        </div>
      </div>
      {children}
    </>
  );
}
